/**
 * SignalDetailPopover — small focused popover that opens when a user clicks
 * one of the Cong / Insid / Call / Earn signal cells in the holdings table.
 * Steve directive 2026-05-22: "just the specific information related to where
 * I click", not the giant StockStoryDrawer.
 *
 * Reuses /api/stock-story/[symbol] (already loaded into useResource cache by
 * the drawer) and renders only the section relevant to the signal type:
 *   - 'cong'    → Congressional trades (top 5) + buy/sell totals + links
 *   - 'insider' → Form 4 net-buy ratio + counts (data/insider-trading.json)
 *   - 'call'    → Latest earnings-call summary (tone + headline + oneLiner + key quotes)
 *   - 'earn'    → epsRevision z-score + last earnings surprise + period
 *
 * Centered modal with backdrop. Click outside or press Esc to close. Compact
 * size (~360px wide) compared to the half-screen drawer.
 */
'use client';

import { useEffect } from 'react';
import { useResource } from '@/lib/use-resource';

export type SignalType = 'cong' | 'insider' | 'call' | 'earn';

interface CongTradeRow {
  transactionDate: string;
  disclosureDate: string;
  legislator: string;
  chamber: 'Senate' | 'House';
  district: string;
  owner: string;
  type: string;
  amount: string;
  link: string;
}
interface StoryResp {
  symbol: string;
  congressionalActivity?: {
    totalLast90Days: number;
    buyCount: number;
    sellCount: number;
    trades: CongTradeRow[];
  } | null;
  latestTranscript?: {
    period: string;
    callDate: string;
    headline: string;
    tone: 'positive' | 'neutral' | 'cautious' | 'negative';
    oneLiner: string;
    guidanceChanges?: string;
    keyQuotes?: Array<{ speaker: string; quote: string; context?: string }>;
    risksFlagged?: string;
  } | null;
  earningsMomentum?: {
    epsRevision: number | null;
    earningsSurprisePct: number | null;
    signal: 'pos' | 'neg' | 'flat' | 'none';
  } | null;
  lastEarnings?: {
    date?: string;
    period?: string;
    epsActual?: number;
    epsEstimate?: number;
    surprisePct?: number;
    yoyGrowthPct?: number | null;
  } | null;
}

interface InsiderRow {
  buyCount?: number; sellCount?: number; buyShares?: number; sellShares?: number;
  buyValue?: number; sellValue?: number; netBuyRatio?: number;
  lastBuyDate?: string | null; lastSellDate?: string | null; form4Count?: number;
}
interface InsiderFile { stocks?: Record<string, InsiderRow> }

const SECTION_TITLE: Record<SignalType, string> = {
  cong:    'Congressional Activity (last 90d)',
  insider: 'Insider Trading (last 6mo)',
  call:    'Latest Earnings Call',
  earn:    'Earnings Momentum',
};

export function SignalDetailPopover({
  symbol,
  signalType,
  onClose,
}: {
  symbol: string;
  signalType: SignalType;
  onClose: () => void;
}) {
  const { data, loading } = useResource<StoryResp>(`/api/stock-story/${symbol}`, 60_000);
  const { data: insiderFile } = useResource<InsiderFile>('/api/insider-trading', 300_000);
  const insider = insiderFile?.stocks?.[symbol] ?? null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[55] bg-black/60 flex items-start justify-center pt-20 px-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0a0e14] border border-[#1e293b] rounded-lg shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0a0e14] border-b border-[#1e293b] px-4 py-2 flex items-center justify-between">
          <div>
            <h3 className="text-[13px] font-bold text-white">
              {symbol} <span className="text-[#64748b] text-[11px] font-normal">— {SECTION_TITLE[signalType]}</span>
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#94a3b8] hover:text-white text-[14px] px-2"
            aria-label="Close"
          >✕</button>
        </div>

        <div className="px-4 py-3 text-[12px] text-[#cbd5e1]">
          {loading && <div className="text-[11px] text-[#64748b] py-4">Loading…</div>}
          {!loading && signalType === 'cong'    && <CongBody story={data} />}
          {!loading && signalType === 'insider' && <InsiderBody insider={insider} symbol={symbol} />}
          {!loading && signalType === 'call'    && <CallBody story={data} />}
          {!loading && signalType === 'earn'    && <EarnBody story={data} />}
        </div>
      </div>
    </div>
  );
}

function CongBody({ story }: { story: StoryResp | null }) {
  const c = story?.congressionalActivity;
  if (!c || c.totalLast90Days === 0) {
    return <div className="text-[11px] text-[#64748b] italic">No Congressional disclosures in the last 90 days.</div>;
  }
  return (
    <div className="space-y-3">
      <div className="text-[11px]">
        <span className="text-emerald-300 font-mono">{c.buyCount} buy</span>
        <span className="text-[#64748b] mx-2">·</span>
        <span className="text-red-300 font-mono">{c.sellCount} sell</span>
        <span className="text-[#64748b] mx-2">·</span>
        <span className="text-[#94a3b8]">{c.totalLast90Days} total disclosures</span>
      </div>
      <ul className="space-y-1.5">
        {c.trades.slice(0, 10).map((t, i) => (
          <li key={`${t.disclosureDate}-${t.legislator}-${i}`} className="grid grid-cols-[68px_1fr_auto] gap-2 items-baseline border-b border-[#1e293b]/40 pb-1">
            <span className="text-[10px] text-[#64748b] font-mono tabular-nums">{t.transactionDate}</span>
            <span className="text-[11px] text-white truncate">
              {t.legislator}
              <span className="text-[#64748b] text-[10px] ml-1">({t.chamber}-{t.district}{t.owner && t.owner !== 'Self' ? ` · ${t.owner}` : ''})</span>
            </span>
            <span className="flex items-baseline gap-2">
              <span className={`text-[10px] font-mono uppercase ${
                /purchase/i.test(t.type) ? 'text-emerald-300' :
                /sale|sold/i.test(t.type) ? 'text-red-300' :
                'text-[#94a3b8]'
              }`}>{t.type.toUpperCase()}</span>
              <span className="text-[10px] text-[#94a3b8] font-mono">{t.amount}</span>
              {t.link && <a href={t.link} target="_blank" rel="noopener" className="text-[#3b82f6] text-[10px] hover:underline">↗</a>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InsiderBody({ insider, symbol }: { insider: InsiderRow | null; symbol: string }) {
  if (!insider || (insider.form4Count ?? 0) === 0) {
    return <div className="text-[11px] text-[#64748b] italic">No Form 4 insider filings on file for {symbol}.</div>;
  }
  const ratio = insider.netBuyRatio ?? null;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 text-[11px]">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-emerald-300/70">Buys</div>
          <div className="font-mono text-emerald-300">{insider.buyCount ?? 0} filings</div>
          {insider.buyValue != null && (
            <div className="text-[10px] text-[#94a3b8]">${(insider.buyValue / 1e6).toFixed(1)}M</div>
          )}
          {insider.lastBuyDate && (
            <div className="text-[9px] text-[#64748b]">last: {insider.lastBuyDate}</div>
          )}
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-red-300/70">Sells</div>
          <div className="font-mono text-red-300">{insider.sellCount ?? 0} filings</div>
          {insider.sellValue != null && (
            <div className="text-[10px] text-[#94a3b8]">${(insider.sellValue / 1e6).toFixed(1)}M</div>
          )}
          {insider.lastSellDate && (
            <div className="text-[9px] text-[#64748b]">last: {insider.lastSellDate}</div>
          )}
        </div>
      </div>
      <div className="text-[11px] border-t border-[#1e293b] pt-2">
        Net-buy ratio:{' '}
        <span className={`font-mono ${
          ratio == null ? 'text-[#64748b]' :
          ratio > 0.15 ? 'text-emerald-300' :
          ratio < -0.15 ? 'text-red-300' :
          'text-amber-300/80'
        }`}>
          {ratio == null ? '—' : ratio.toFixed(2)}
        </span>
        <span className="text-[10px] text-[#64748b] ml-2">across {insider.form4Count ?? 0} Form 4 filings</span>
      </div>
      <div className="text-[10px] text-[#64748b] italic leading-snug">
        Range -1 (all selling) to +1 (all buying). Market-cap normalized: $10M buying on $5B mcap is signal, $86K selling on $115B mcap is noise.
      </div>
    </div>
  );
}

function CallBody({ story }: { story: StoryResp | null }) {
  const t = story?.latestTranscript;
  if (!t) {
    return <div className="text-[11px] text-[#64748b] italic">No earnings-call summary yet for this stock.</div>;
  }
  const toneClass =
    t.tone === 'positive' ? 'text-emerald-300' :
    t.tone === 'negative' ? 'text-red-300' :
    t.tone === 'cautious' ? 'text-amber-300' :
    'text-[#cbd5e1]';
  return (
    <div className="space-y-2">
      <div className="text-[10px] text-[#64748b] font-mono">
        {t.callDate} · {t.period} · <span className={`uppercase ${toneClass}`}>{t.tone}</span>
      </div>
      <div className="text-[12px] font-semibold text-white leading-snug">{t.headline}</div>
      <div className="text-[11px] text-[#cbd5e1] leading-snug italic">{t.oneLiner}</div>
      {t.guidanceChanges && (
        <div className="border-t border-[#1e293b] pt-2">
          <div className="text-[9px] uppercase tracking-wider text-emerald-300/70 mb-1">Guidance</div>
          <div className="text-[11px] leading-snug">{t.guidanceChanges}</div>
        </div>
      )}
      {t.risksFlagged && (
        <div>
          <div className="text-[9px] uppercase tracking-wider text-red-300/70 mb-1">Risks flagged</div>
          <div className="text-[11px] leading-snug">{t.risksFlagged}</div>
        </div>
      )}
      {t.keyQuotes && t.keyQuotes.length > 0 && (
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#94a3b8] mb-1">Key quotes</div>
          <ul className="space-y-1.5">
            {t.keyQuotes.slice(0, 3).map((q, i) => (
              <li key={i} className="border-l-2 border-[#1e293b] pl-2">
                <div className="text-[11px] text-white italic leading-snug">&ldquo;{q.quote}&rdquo;</div>
                <div className="text-[9px] text-[#64748b] mt-0.5">— {q.speaker}{q.context ? ` · ${q.context}` : ''}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Hero verdict pill from most-recent earnings surprise %. Single-glance
// "what happened" — anchored on the surprise sign + magnitude, not the
// blended z-score (which can look meek even after a monster beat because
// analyst-revision direction is slow).
//   > +10%  → BIG BEAT       (emerald)
//   2..10%  → MODEST BEAT    (emerald)
//   ±2%     → INLINE         (slate)
//   -10..-2% → MODEST MISS    (red)
//   < -10%  → BIG MISS       (red)
function surpriseVerdict(surprisePct: number | null | undefined): { label: string; tone: 'beat-big' | 'beat' | 'inline' | 'miss' | 'miss-big' | 'none' } {
  if (surprisePct == null || !Number.isFinite(surprisePct)) return { label: 'NO DATA', tone: 'none' };
  if (surprisePct > 10)  return { label: 'BIG BEAT',     tone: 'beat-big' };
  if (surprisePct > 2)   return { label: 'MODEST BEAT',  tone: 'beat' };
  if (surprisePct >= -2) return { label: 'INLINE',       tone: 'inline' };
  if (surprisePct >= -10) return { label: 'MODEST MISS', tone: 'miss' };
  return { label: 'BIG MISS', tone: 'miss-big' };
}

function verdictClasses(tone: ReturnType<typeof surpriseVerdict>['tone']): string {
  switch (tone) {
    case 'beat-big':  return 'bg-emerald-500/15 border-emerald-400/50 text-emerald-300';
    case 'beat':      return 'bg-emerald-500/10 border-emerald-400/30 text-emerald-300/90';
    case 'inline':    return 'bg-[#1e293b] border-[#334155] text-[#cbd5e1]';
    case 'miss':      return 'bg-red-500/10 border-red-400/30 text-red-300/90';
    case 'miss-big':  return 'bg-red-500/15 border-red-400/50 text-red-300';
    default:          return 'bg-[#0f141c] border-[#1e293b] text-[#64748b]';
  }
}

// Analyst-direction arrow + plain-English label from epsRevision z-score.
// Same scale used by the holdings-table Earn cell, surfaced here in human form.
function analystDirection(z: number | null | undefined): { arrow: string; label: string; tone: string } {
  if (z == null || !Number.isFinite(z)) return { arrow: '—', label: 'no data', tone: 'text-[#64748b]' };
  if (z >  1.0) return { arrow: '↑↑', label: 'strongly raising',  tone: 'text-emerald-300' };
  if (z >  0.3) return { arrow: '↑',  label: 'raising',           tone: 'text-emerald-300/85' };
  if (z >= -0.3) return { arrow: '→', label: 'flat',              tone: 'text-[#cbd5e1]' };
  if (z >= -1.0) return { arrow: '↓', label: 'cutting',           tone: 'text-red-300/85' };
  return                  { arrow: '↓↓', label: 'strongly cutting', tone: 'text-red-300' };
}

interface EpsHistoryPoint { date: string; eps1y: number | null; eps2y: number | null }
interface EpsHistoryResp { bySymbol: Record<string, EpsHistoryPoint[]> }

// Minimal inline FY1-only sparkline. Self-contained SVG, no recharts — keeps
// the popover lightweight. ~120×28 px. Stroke color picks up direction tone.
function EpsSparkline({ points }: { points: EpsHistoryPoint[] }) {
  const xs = points.filter(p => p.eps1y != null).map(p => p.eps1y as number);
  if (xs.length < 2) {
    return <span className="text-[10px] text-[#64748b] italic">{xs.length === 0 ? 'no history yet' : 'one snapshot — waiting for second'}</span>;
  }
  const W = 120, H = 28, PAD = 2;
  const min = Math.min(...xs), max = Math.max(...xs);
  const range = max - min || 1;
  const stepX = (W - 2 * PAD) / (xs.length - 1);
  const path = xs
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${(PAD + i * stepX).toFixed(1)} ${(PAD + (1 - (v - min) / range) * (H - 2 * PAD)).toFixed(1)}`)
    .join(' ');
  // Color from sparkline's OWN slope, not the z-score — a +1.6% walk-up over
  // 30d should render green even when the blended z is meek (analyst-revision
  // z incorporates recency weighting that under-shows steady positive drift).
  const sparkDeltaPct = (xs[xs.length - 1] - xs[0]) / xs[0] * 100;
  const color = sparkDeltaPct > 0.3 ? '#10b981' : sparkDeltaPct < -0.3 ? '#ef4444' : '#94a3b8';
  return (
    <svg width={W} height={H} className="overflow-visible">
      {/* Baseline at the first observation so up = raising vs the start */}
      <line x1={PAD} y1={PAD + (1 - (xs[0] - min) / range) * (H - 2 * PAD)} x2={W - PAD} y2={PAD + (1 - (xs[0] - min) / range) * (H - 2 * PAD)} stroke="#334155" strokeDasharray="2 2" strokeWidth="1" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx={W - PAD} cy={PAD + (1 - (xs[xs.length - 1] - min) / range) * (H - 2 * PAD)} r="2" fill={color} />
    </svg>
  );
}

function EarnBody({ story }: { story: StoryResp | null }) {
  const em = story?.earningsMomentum;
  const le = story?.lastEarnings;
  // Pull the FY1 EPS history for the inline sparkline. Cached 5 min — same
  // shared cache the per-stock drawer / Movers tab use, so no extra wire cost.
  const { data: epsHist } = useResource<EpsHistoryResp>(
    story?.symbol ? `/api/eps-revision-history?symbols=${story.symbol}` : null,
    300_000,
  );
  if (!em || em.signal === 'none') {
    return <div className="text-[11px] text-[#64748b] italic">No analyst revisions or recent earnings surprise on file.</div>;
  }
  // surprisePct on lastEarnings is in PERCENT units (71.3 == 71.3%); on
  // earningsMomentum it's a DECIMAL (0.713 == 71.3%) — different upstream
  // pipelines. The hero verdict and last-earnings line both use the percent
  // form via le.surprisePct; the side tile uses em.earningsSurprisePct × 100.
  const verdict = surpriseVerdict(le?.surprisePct);
  const direction = analystDirection(em.epsRevision);
  const points = (story?.symbol && epsHist?.bySymbol?.[story.symbol]) || [];
  return (
    <div className="space-y-3">
      {/* Hero verdict — visual anchor. The most-recent surprise is what */}
      {/* you need to see first; analyst direction is supporting detail. */}
      <div className="flex items-center gap-3">
        <div className={`inline-flex items-center px-3 py-1 rounded-md border text-[12px] font-bold tracking-wide ${verdictClasses(verdict.tone)}`}>
          {verdict.label}
        </div>
        {le?.period && (
          <div className="text-[10px] text-[#94a3b8] font-mono">
            {le.period}{le.date ? ` · reported ${le.date}` : ''}
          </div>
        )}
      </div>

      {/* Last earnings detail — single readable sentence */}
      {le && (le.epsActual != null || le.epsEstimate != null) && (
        <div className="text-[11px] text-[#cbd5e1]">
          EPS <span className="font-mono text-white">${le.epsActual?.toFixed(2) ?? '—'}</span>
          {' vs '}
          <span className="font-mono">${le.epsEstimate?.toFixed(2) ?? '—'}</span> est
          {le.surprisePct != null && (
            <>
              {' · '}
              <span className={`font-mono font-semibold ${le.surprisePct >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                {le.surprisePct >= 0 ? '+' : ''}{le.surprisePct.toFixed(1)}%
              </span>
            </>
          )}
          {le.yoyGrowthPct != null && (
            <span className="text-[10px] text-[#94a3b8] ml-2">
              · Y/Y <span className={`font-mono ${le.yoyGrowthPct >= 0 ? 'text-emerald-300/80' : 'text-red-300/80'}`}>
                {le.yoyGrowthPct >= 0 ? '+' : ''}{le.yoyGrowthPct.toFixed(1)}%
              </span>
            </span>
          )}
        </div>
      )}

      {/* Analyst direction — plain-English label + arrow + sparkline */}
      <div className="border-t border-[#1e293b] pt-2">
        <div className="text-[9px] uppercase tracking-wider text-[#94a3b8] mb-1.5">Analyst direction · forward EPS</div>
        <div className="flex items-center gap-3">
          <div className={`flex items-baseline gap-1.5 ${direction.tone}`}>
            <span className="text-[18px] leading-none font-mono">{direction.arrow}</span>
            <span className="text-[11px] font-semibold">{direction.label}</span>
            {em.epsRevision != null && (
              <span className="text-[10px] text-[#64748b] font-mono ml-1">
                ({em.epsRevision >= 0 ? '+' : ''}{em.epsRevision.toFixed(2)}σ)
              </span>
            )}
          </div>
          <div className="ml-auto"><EpsSparkline points={points} /></div>
        </div>
        {points.length >= 2 && (() => {
          const xs = points.filter(p => p.eps1y != null).map(p => p.eps1y as number);
          if (xs.length < 2) return null;
          const delta = (xs[xs.length - 1] - xs[0]) / xs[0] * 100;
          return (
            <div className="text-[10px] text-[#64748b] mt-1">
              FY1 consensus <span className={`font-mono ${delta >= 0 ? 'text-emerald-300/70' : 'text-red-300/70'}`}>
                {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
              </span>
              <span className="text-[#64748b]"> over {points.length} snapshots ({points[0].date} → {points[points.length - 1].date})</span>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
